<script lang="ts">
// Plain `<script>`, not `setup`: the bridge below is module scope — one listener, one session token
// and one resolver registry shared by every banner instance on the page.
/**
 * Braze banner action handling. Banners render as HTML in an iframe; most CTAs run in place via
 * `useContentActions` — the only CTA that leaves the page is `navigate`.
 *
 * 1. Single CTA (default, no-code): CRM sets the banner custom properties `action`
 *    (a CONTENT_ACTIONS value), optional `couponCode` / `productId` / `url` and optional `ctaText`.
 *    The whole banner becomes one click target. Config comes from the trusted SDK.
 *
 *    Optional `braze_campaign_name` / `braze_campaign_api_id` properties add attribution.
 *
 * 2. Custom Code block: the banner posts one message per interaction to the module-level listener
 *    below. The SDK renders banners as a same-origin `srcdoc` iframe (it injects `brazeBridge` by
 *    reaching into `contentWindow`), so `event.origin` is our own origin and proves nothing. Trust
 *    comes from matching `event.source` to a mounted banner's iframe, plus the `mhj-banner` tag, a
 *    per-session token, the CONTENT_ACTIONS allow-list and sanitized params.
 *
 *    `placement_id` and campaign attribution are taken from the SDK banner the posting frame belongs
 *    to, never from the message, so no campaign-specific values need authoring (#262226 AC6).
 *
 *    `action` is optional: supply one to run a storefront action, omit it to only log a click for
 *    something the banner already did itself (e.g. the #260908 newsletter form). Either way the
 *    banner must NOT set the `action` custom property — that turns on mode 1, which suppresses
 *    pointer events inside the iframe. The banner must also call `brazeBridge.logClick()` itself.
 *
 * Custom Code snippet:
 *   <button onclick="brazeBridge.logClick(); parent.postMessage({
 *     source: 'mhj-banner',
 *     action: 'addToBag',                // any CONTENT_ACTIONS value; omit to only log the click
 *     ctaText: 'Add to bag',             // label reported to the banner_click event
 *     params: { productId: '...' },      // optional
 *     token: (parent.__MHJ_BANNER_TOKEN__ || '')
 *   }, '*')">Add to bag</button>
 */
declare global {
  interface Window {
    __MHJ_BANNER_TOKEN__?: string
  }
}

/** Placement + attribution owned by the parent for a mounted banner, keyed by its iframe. */
type BannerContext = { placementId: string; campaign: CampaignDataLayer | null }
type BannerContextResolver = (source: MessageEventSource | null) => BannerContext | null

// Returns a boolean so a success-gated action can decide whether the click counts.
type RunBannerAction = (
  action: ContentAction,
  params: ContentActionParams,
  campaign: CampaignDataLayer | null,
) => Promise<boolean>
// Snapshots the click context and returns the emitter; shared by the single-CTA click and the bridge.
type CaptureBannerClick = (placementId: string, ctaText: string, campaign: CampaignDataLayer | null) => () => void

let bridgeRegistered = false
const bannerResolvers = new Set<BannerContextResolver>()

const resolveBannerContext = (source: MessageEventSource | null): BannerContext | null => {
  for (const resolve of bannerResolvers) {
    const context = resolve(source)
    if (context) return context
  }
  return null
}

// One shared postMessage listener for all banner instances; no-op until a Custom Code banner posts.
function ensureBannerBridge(runAction: RunBannerAction, captureClick: CaptureBannerClick) {
  if (bridgeRegistered || import.meta.server) return
  bridgeRegistered = true

  if (!window.__MHJ_BANNER_TOKEN__) {
    window.__MHJ_BANNER_TOKEN__ = window.crypto?.randomUUID?.() ?? String(Math.random()).slice(2)
  }

  window.addEventListener('message', async (event: MessageEvent) => {
    const message = decodeBannerMessage(event, window.__MHJ_BANNER_TOKEN__)
    if (!message) return

    const owner = resolveBannerContext(event.source)
    // Placement and attribution always come from the SDK banner owning the posting frame, so a frame
    // can neither pick its own placement nor forge attribution (#262226 AC6).
    if (!owner) return

    const { placementId, campaign } = owner
    const ctaText = sanitizeCampaignText(message.ctaText) || 'banner - no ctaText'

    // Attribution only exists if the template carries the campaign Liquid properties (#259128); a
    // hand-built Custom Code template is the case authors forget, and it fails silently otherwise.
    if (import.meta.dev && !campaign) {
      console.warn(
        `[BrazeBanner] "${placementId}" posted a banner_click with no campaign attribution. Add the ` +
          'braze_campaign_name / braze_campaign_api_id Liquid properties to the banner template (#259128).',
      )
    }

    // Snapshot before any action runs: a gated `navigate` changes the page out from under us.
    const logClick = captureClick(placementId, ctaText, campaign)

    if (!message.action) {
      logClick()
      return
    }

    if (SUCCESS_GATED_ACTIONS.includes(message.action)) {
      if (await runAction(message.action, message.params, campaign)) logClick()
      return
    }

    logClick()
    runAction(message.action, message.params, campaign)
  })
}
</script>

<script setup lang="ts">
import type { Banner } from '@braze/web-sdk'
import * as Sentry from '@sentry/nuxt'

type brazeBannerProps = {
  /**
   * Braze placement IDs in priority order (authored in Contentful): the first one with a served
   * banner wins, the rest are fallbacks. Audience targeting is handled by each campaign's segment.
   */
  placementIds?: string[]
  variant?: 'unknown'
  /** `default` caps at 1248px on desktop, `full-width` fills. PanelSwitcher passes this directly. */
  width?: 'default' | 'full-width'
  supportingData?: {
    home?: {
      width?: { value?: 'default' | 'full-width' }
      marginBottom?: { value?: boolean }
    }
  }
}
const props = defineProps<brazeBannerProps>()

const bannerWidth = computed(() => props.width || props.supportingData?.home?.width?.value || 'default')
const isFullWidth = computed(() => bannerWidth.value === 'full-width')
const hasMarginBottom = computed(() => !!props.supportingData?.home?.marginBottom?.value)
const emit = defineEmits<{
  action: [action: ContentAction, params: ContentActionParams]
  // Resolved placement state, so consumers (e.g. the debug page) can show what was really served.
  resolved: [
    { state: 'served' | 'control' | 'empty' | 'suppressed'; id: string | null; requested: string[]; reason?: string },
  ]
}>()
const { $braze } = useNuxtApp()
const { dispatch } = useContentActions()
const campaignRules = useBrazeCampaignRules()

// No locale suffixing or fallback: each placement is served exactly as named.
const placementIds = computed<string[]>(() => (props.placementIds ?? []).map((id) => id?.trim()).filter(Boolean))

function requestBanners() {
  const ids = placementIds.value
  if (ids.length) $braze.sdk.requestBannersRefresh(ids)
  // Braze fires no banners-update when a placement serves nothing, so an already-inserted banner would
  // linger after its cache was invalidated (changeUser/wipe). Re-check once the refresh has settled,
  // but never force-empty a first request whose (rate-limited) refresh simply hasn't landed yet.
  clearTimeout(emptyCheckTimer)
  emptyCheckTimer = setTimeout(() => scheduleRender({ keepIfEmpty: !currentBanner.value }), 2000)
}

// renderBanner is awaited nowhere — it runs from SDK callbacks and timers, where a rejection would
// surface as an unhandled promise rejection instead of anything we can act on.
function scheduleRender(options?: { keepIfEmpty?: boolean }) {
  renderBanner(options).catch((error) => Sentry.captureException(error, { tags: { feature: 'braze-banner' } }))
}

const bannerContainer = ref<HTMLElement | null>(null)
const eligible = ref<boolean | null>(null)
const currentBanner = ref<Banner | null>(null)
// The placement id that actually served the shown banner, reported as `placement_id` on banner_click.
const resolvedPlacementId = ref<string | null>(null)
// Set in single-CTA mode when the banner declares an `action` custom property.
const bannerAction = ref<BannerAction | null>(null)
// Read from every served banner, including Custom Code banners that declare no `action`, so a posted
// message can still be attributed without the campaign authoring anything extra.
const bannerCampaign = ref<CampaignDataLayer | null>(null)
// Delayed empty-check so a confirmed-empty re-request clears any stale, previously inserted banner.
let emptyCheckTimer: ReturnType<typeof setTimeout> | undefined

// Click logging is the caller's job: single-CTA via logBannerClick(), Custom Code via the iframe's
// own brazeBridge.logClick().
async function runBannerAction(
  action: ContentAction,
  params: ContentActionParams,
  campaign: CampaignDataLayer | null,
): Promise<boolean> {
  if (!CONTENT_ACTIONS.includes(action)) return false
  emit('action', action, params)
  return dispatch(action, params, campaign)
}

// One placement id is reused across many Contentful pages, so `page_url` records where the click
// happened; it and `content_type` are snapshotted before a gated `navigate` can change them.
function captureBannerClick(placementId: string, ctaText: string, campaign: CampaignDataLayer | null) {
  const pageUrl = window.location.href
  const contentType = usePageContentType().contentType.value
  return () => {
    useBrazeTracking().logCustomEvent(
      'banner_click',
      {
        page_url: pageUrl,
        placement_id: placementId,
        cta_text: ctaText,
        ...campaign,
      },
      { contentType },
    )
  }
}

type BannerAction = {
  action: ContentAction
  params: ContentActionParams
  ctaText: string
  campaign: CampaignDataLayer | null
}

// Read the single-CTA config from the banner's Braze custom properties.
function readBannerAction(banner: Banner): BannerAction | null {
  const action = banner.getStringProperty('action')
  if (!isContentAction(action)) return null
  return {
    action,
    // Pointer events are suppressed inside the iframe, so the clicked element can't be identified —
    // the reporting label must come from the SDK config CRM authored.
    ctaText: sanitizeCampaignText(banner.getStringProperty('ctaText')) || 'banner - no ctaText',
    campaign: readBrazeCampaign((key) => banner.getStringProperty(key), 'banner'),
    params: sanitizeParams({
      couponCode: banner.getStringProperty('couponCode'),
      productId: banner.getStringProperty('productId'),
      url: banner.getStringProperty('url'),
    }),
  }
}

function applyBannerConfig(banner: Banner) {
  bannerAction.value = readBannerAction(banner)
  bannerCampaign.value = readBrazeCampaign((key) => banner.getStringProperty(key), 'banner')
}

// Prevent a late rules verdict from overtaking a newer render.
let renderToken = 0

type BannerPick =
  | { kind: 'stale' }
  | { kind: 'served'; banner: Banner; id: string }
  | { kind: 'suppressed'; id: string; reason: string }
  | { kind: 'empty' }

/** Suppressed placements are never inserted, avoiding impressions while preserving fallbacks. */
async function pickBanner(ids: string[], token: number): Promise<BannerPick> {
  let suppressed: { id: string; reason: string } | undefined
  for (const id of ids) {
    const banner = $braze.sdk.getBanner(id)
    if (!banner) continue
    // Rules gate insertion, not removal: the banner on screen was checked when it was inserted and
    // keeps its place even if the state later fails its rules (docs/braze-campaign-rules.md).
    if (banner.id === currentBanner.value?.id) return { kind: 'served', banner, id }
    const verdict = await campaignRules.evaluateAsync(banner.getStringProperty('rules') ?? undefined)
    if (token !== renderToken) return { kind: 'stale' }
    if (verdict.show) return { kind: 'served', banner, id }
    suppressed ??= { id, reason: verdict.reason ?? 'rules not met' }
  }
  return suppressed ? { kind: 'suppressed', ...suppressed } : { kind: 'empty' }
}

/**
 * `keepIfEmpty` retains the shown banner while the cache is transiently empty during an identity
 * switch (login -> changeUser invalidates banners), avoiding a flash of no banner.
 */
async function renderBanner({ keepIfEmpty = false }: { keepIfEmpty?: boolean } = {}) {
  const ids = placementIds.value
  if (!bannerContainer.value || !ids.length) return

  const token = ++renderToken
  const pick = await pickBanner(ids, token)
  // Re-read after the await: a newer render, or an unmount, may have overtaken this one.
  const container = bannerContainer.value
  if (pick.kind === 'stale' || token !== renderToken || !container) return

  if (pick.kind !== 'served') {
    const suppressed = pick.kind === 'suppressed' ? pick : undefined
    // A suppression is definitive; `keepIfEmpty` only covers a transiently empty cache.
    if (keepIfEmpty && !suppressed) return
    emit('resolved', {
      state: suppressed ? 'suppressed' : 'empty',
      id: suppressed?.id ?? null,
      requested: ids,
      reason: suppressed?.reason,
    })
    eligible.value = false
    currentBanner.value = null
    resolvedPlacementId.value = null
    bannerAction.value = null
    bannerCampaign.value = null
    container.innerHTML = ''
    container.style.display = 'none'
    return
  }

  const { banner, id: resolvedId } = pick

  // renderBanner runs many times per view and `insertBanner` logs an impression on every call, so
  // skip the re-insert for an already-shown banner and just keep the resolved CTA in sync.
  if (currentBanner.value?.id === banner.id) {
    clearTimeout(emptyCheckTimer)
    applyBannerConfig(banner)
    return
  }

  emit('resolved', { state: banner.isControl ? 'control' : 'served', id: resolvedId, requested: ids })
  eligible.value = true
  currentBanner.value = banner
  resolvedPlacementId.value = resolvedId
  // A banner is served — cancel the pending empty-check so it can't wipe this render.
  clearTimeout(emptyCheckTimer)
  // insertBanner does NOT replace existing content, so clear first or the old iframe sticks around.
  container.innerHTML = ''
  $braze.sdk.insertBanner(banner, container)

  // Control variant: still inserted (so the impression fires) but visually collapsed.
  container.style.display = banner.isControl ? 'none' : ''

  applyBannerConfig(banner)
}

// Tap vs scroll/drag detection (#256129 AC4): on touch the whole-banner CTA must only fire on a
// genuine tap, never while the shopper scrolls or drags over it.
const DRAG_THRESHOLD_PX = 10
let pointerStart: { x: number; y: number } | null = null
let pointerMoved = false

function onPointerDown(event: PointerEvent) {
  pointerStart = { x: event.clientX, y: event.clientY }
  pointerMoved = false
}

function onPointerMove(event: PointerEvent) {
  if (!pointerStart) return
  if (
    Math.abs(event.clientX - pointerStart.x) > DRAG_THRESHOLD_PX ||
    Math.abs(event.clientY - pointerStart.y) > DRAG_THRESHOLD_PX
  ) {
    pointerMoved = true
  }
}

// The browser fires pointercancel when it takes the gesture over for scrolling — treat that as a drag.
function onPointerCancel() {
  pointerMoved = true
}

async function onBannerClick() {
  // Reset on every activation, or later hover movement (no button down) would flag `pointerMoved`
  // and wrongly swallow the next keyboard activation.
  const wasDrag = pointerMoved
  pointerMoved = false
  pointerStart = null
  if (wasDrag) return
  if (!bannerAction.value || !currentBanner.value || !resolvedPlacementId.value) return
  const { action, params, ctaText, campaign } = bannerAction.value
  const placementId = resolvedPlacementId.value
  // We suppressed the iframe's pointer events, so log the native banner click ourselves. Braze's own
  // click metric stays at click time regardless of whether the action goes on to succeed.
  $braze?.sdk?.logBannerClick(currentBanner.value)

  // Whole banner is the single click target; report the authored CTA label (or its fallback).
  const logClick = captureBannerClick(placementId, ctaText, campaign)

  if (SUCCESS_GATED_ACTIONS.includes(action)) {
    if (await runBannerAction(action, params, campaign)) logClick()
    return
  }
  logClick()
  runBannerAction(action, params, campaign)
}

// Matches a posted message back to this banner via its inserted iframe, so `placement_id` and
// campaign attribution come from the SDK instead of anything the untrusted frame claims.
const resolveOwnContext: BannerContextResolver = (source) => {
  if (!source || !resolvedPlacementId.value) return null
  const frame = bannerContainer.value?.querySelector('iframe')
  if (!frame || frame.contentWindow !== source) return null
  return { placementId: resolvedPlacementId.value, campaign: bannerCampaign.value }
}

onMounted(() => {
  ensureBannerBridge(runBannerAction, captureBannerClick)
  bannerResolvers.add(resolveOwnContext)

  // Track the identity to detect the post-login `changeUser()`, which invalidates the cached banners.
  // The SDK also restores a sticky user asynchronously, so `getUserId()` can go null -> cid during
  // hydration; always render anyway or an already-cached placement (no further update) stays blank.
  let lastUserId = $braze.sdk.getUser()?.getUserId() ?? null

  // Subscribe before refreshing so the callback fires with the latest cached/served banner.
  $braze.sdk.subscribeToBannersUpdates(() => {
    const userId = $braze.sdk.getUser()?.getUserId() ?? null
    const identityChanged = userId !== lastUserId
    if (identityChanged) {
      lastUserId = userId
      requestBanners()
    }
    // Keep the shown banner if the cache is momentarily empty mid-switch; the re-request will render it.
    scheduleRender({ keepIfEmpty: identityChanged })
  })

  // Render whatever's already cached — Braze auto-publishes the last cached banners on session start
  // / changeUser for free, and the 5-token refresh budget is too small to spend on every mount.
  // https://www.braze.com/docs/user_guide/channels/banners#rate-limiting-for-refresh-requests
  scheduleRender({ keepIfEmpty: true })

  // Auto-publish only republishes placements cached in a PRIOR session, so a fresh browser renders
  // nothing until we request explicitly. The watch below isn't `immediate`, so it can't cover this.
  requestBanners()

  // Watch the joined ids (a primitive), not the array ref: a parent re-render passing an equal array
  // must not re-fire requestBannersRefresh, which Braze rate-limits to a small burst.
  watch(
    () => placementIds.value.join('\n'),
    () => {
      renderToken++
      // Clear first so nothing stale flashes; the post-refresh subscription owns the next render.
      const container = bannerContainer.value
      if (container) {
        container.innerHTML = ''
        container.style.display = 'none'
      }
      currentBanner.value = null
      bannerAction.value = null
      eligible.value = null
      requestBanners()
    },
  )
})

onUnmounted(() => {
  clearTimeout(emptyCheckTimer)
  bannerResolvers.delete(resolveOwnContext)
})
</script>

<template>
  <div
    ref="bannerContainer"
    data-testid="braze-banner"
    class="braze-banner"
    :class="{
      'braze-banner--clickable': !!bannerAction,
      'braze-banner--full-width': isFullWidth,
      'braze-banner--margin-bottom': hasMarginBottom,
      /* Page-level spacing rules auto-add a bottom margin unless the class contains
         `--no-margin-bottom` — same escape hatch as ColumnPanel/RichTextPanel. */
      'braze-banner--no-margin-bottom': !hasMarginBottom,
    }"
    :role="bannerAction ? 'button' : undefined"
    :tabindex="bannerAction ? 0 : undefined"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointercancel="onPointerCancel"
    @click="onBannerClick"
    @keydown.enter.prevent="onBannerClick"
    @keydown.space.prevent="onBannerClick"
  ></div>
</template>

<style lang="postcss">
.braze-banner {
  width: 100%;
  padding-inline: var(--space-50); /* 16px, mobile + tablet only */
  max-width: 78rem; /* 1248px */
  margin-inline: auto;

  @media (--desktop) {
    padding-inline: 0;
  }
}
.braze-banner--full-width {
  max-width: none;
}
.braze-banner--margin-bottom {
  margin-bottom: var(--component-space);
}
.braze-banner--clickable {
  cursor: pointer;
}
/* Whole-banner CTA: stop the inserted iframe swallowing the click so it reaches the container. */
.braze-banner--clickable * {
  pointer-events: none;
}
</style>
